"use client";

import { Translate } from "@/components/Translate";
import { sizes } from "@/components/bloks/Asset";
import { Button } from "@/components/ui/Button";
import {
	DrawerClose,
	ModalContent,
	ModalFooter,
	ModalMain,
	ModalRoot,
	ModalTrigger,
	ModalViewport,
} from "@/components/ui/Drawer";
import { TextField } from "@/components/ui/Input";
import { Toggle } from "@/components/ui/ToggleGroup";
import { env } from "@/env.mjs";
import { useTranslations } from "@/i18n/client";
import { useGeo } from "@/i18n/routing";
import type { ProductDetails } from "@/lib/centra";
import { useBackInStock, useSelection } from "@frend-digital/centra/client";
import { DialogClose, classed } from "@frend-digital/ui";
import { memo, useActionState, useCallback, useMemo, useState } from "react";
import pdpStyles from "../ProductDetailsPage.module.css";
import styles from "./BuyBox.module.css";
import { type UseBuyBoxReturn, useBuyBox } from "./useBuyBox";

import { RichTextRender } from "@/components/ui/RichTextRender";
import type { GlobalData } from "@/features/global/dal";
import type { AssetStoryblokCompat } from "@/types/storyblok-compat";
import { getMedia } from "@frend-digital/storyblok";
import { useUrlSearchParams } from "@frend-digital/ui/next";
import { useAtom } from "jotai";
import Image from "next/image";
import { SizeChartTable } from "../SizeChartTable";
import { StockStatusIndicator } from "../StockStatusIndicator/StockStatusIndicator";
import { VolumentalRecommendation, isVolumentalSupported } from "../VolumentalWidget";
import { sizeChartAtom } from "./sizeChartState";

export interface BuyBoxProps {
	sizes: ProductDetails["items"];
	fit: ProductDetails["pr_fit"];
	modelDesc?: string;
	measurementChart: ProductDetails["measurementChart"];
	itemTable: ProductDetails["itemTable"] & { desc?: string };
	sizeChartAssets: GlobalData["sizeChart"];
	preview: boolean;
	isKidsShoes?: boolean;
	kidsShoesDesc?: string;
	sizeTable?: ProductDetails["sizeTable"];
	slug: string[];
}

export type GetSizeProps = (
	item: NonNullable<ProductDetails["items"]>[number],
	props?: Record<string, unknown>,
) => React.ComponentProps<"button">;

interface SizeSelectorProps {
	sizes: ProductDetails["items"];
	getSizeProps: GetSizeProps;
	soldOutTooltip?: boolean;
}

const SizeToggle = classed.button(Toggle, styles.toggle);

type SizeChartKey = Exclude<keyof GlobalData["sizeChart"], "kidsShoesDesc">;

const getChartTypeKey = (desc?: string): SizeChartKey | null => {
	if (!desc) return null;

	switch (desc.toLowerCase()) {
		case "shoes":
			return "shoes";
		case "kids apparel":
		case "kids apparel 2":
		case "kids clothing":
			return "kidsApparel";
		case "kids headwear":
			return "kidsHeadwear";
		case "kids mittens":
			return "kidsMittens";
		default:
			return null;
	}
};

export const BuyBox = memo(
	({
		sizes: size,
		fit,
		modelDesc,
		measurementChart,
		itemTable,
		sizeChartAssets,
		preview,
		isKidsShoes,
		kidsShoesDesc,
		sizeTable,
		slug,
	}: BuyBoxProps) => {
		const buyBox = useBuyBox(size);
		const { getSizeProps, getButtonProps, getButtonLabel, item, selectSize } = buyBox;

		const volumentalProductId = size?.find((i) => i.ean)?.ean;

		// When the Volumental widget renders it replaces the size chart, so hide the "Size chart" link.
		const showVolumental =
			!!env.NEXT_PUBLIC_VOLUMENTAL_CLIENT_ID &&
			!!volumentalProductId &&
			isVolumentalSupported(sizeTable);

		// Volumental's recommended size (EU length) -> select the matching size in the picker.
		const onVolumentalRecommendation = useCallback(
			({ length }: { length?: string }) => {
				const rec = length?.trim();
				const match = rec ? size?.find((i) => i.name?.trim() === rec) : undefined;
				if (match) selectSize(match);
			},
			[size, selectSize],
		);
		const [open, setOpen] = useAtom(sizeChartAtom);
		const buttonProps = getButtonProps();

		const outOfStock = buttonProps["data-type"] === "out_of_stock";

		const { tableData, sizeHeaders, sizesOnXAxis } = useFormattedMeasurementChart(
			measurementChart,
			itemTable,
		);

		const chartTypeKey = getChartTypeKey(itemTable?.desc);

		const chartContentBlok = chartTypeKey ? sizeChartAssets?.[chartTypeKey]?.[0] : null;

		const media = chartContentBlok?.image
			? getMedia(chartContentBlok?.image as AssetStoryblokCompat)
			: undefined;

		return (
			<section id="pdp-buybox">
				<div className={styles.buyBox}>
					<div className={styles.row}>
						{size && size.length > 1 && (
							<>
								<p className={pdpStyles.subtitle}>
									<Translate>Size</Translate>
								</p>
								{!showVolumental && Array.isArray(measurementChart?.contents) && (
									<ModalRoot open={open} onOpenChange={setOpen}>
										<ModalTrigger className={styles.trigger}>
											<span>
												<Translate>Size Chart</Translate>
											</span>
										</ModalTrigger>

										<ModalViewport className={styles.drawer}>
											<ModalContent>
												<ModalMain className={styles.content}>
													<header>
														<DrawerClose className={styles.close} />
														<h2 className={styles.header}>
															<Translate>Size Guide</Translate>
														</h2>
													</header>
													<SizeChartTable
														tableData={tableData}
														sizeHeaders={sizeHeaders}
														sizes={size}
														getSizeProps={getSizeProps}
														itemTable={itemTable}
														isKidsShoes={isKidsShoes}
														kidsShoesDesc={kidsShoesDesc}
														sizesOnXAxis={sizesOnXAxis}
													/>

													{chartContentBlok && (
														<>
															{media && (
																<div className={styles.productTypeImageContainer}>
																	<Image
																		src={media.url}
																		width={media.width}
																		height={media.height}
																		alt=""
																		sizes={sizes(100, 60)}
																	/>
																</div>
															)}
															<div className={styles.richTextContainer}>
																{chartContentBlok?.description && (
																	<RichTextRender
																		blok={chartContentBlok.description}
																		preview={preview}
																	/>
																)}
															</div>
														</>
													)}
												</ModalMain>
												<ModalFooter className={styles.footer}>
													<DialogClose asChild>
														<Button>
															<Translate>Close</Translate>
														</Button>
													</DialogClose>
												</ModalFooter>
											</ModalContent>
										</ModalViewport>
									</ModalRoot>
								)}
							</>
						)}
					</div>

					<SizeSelector sizes={size} getSizeProps={getSizeProps} soldOutTooltip />

					<div className={styles.fitContainer}>
						{fit && (
							<span className={styles.fit}>
								<span className={styles.fitTitle}>
									<Translate>Fit: </Translate>
								</span>
								{fit}
							</span>
						)}
						{item && <StockStatusIndicator stock={item.stock} />}
					</div>
					{modelDesc && chartTypeKey === "kidsApparel" && (
						<p className={styles.modelDesc}>{modelDesc}</p>
					)}
				</div>

				{showVolumental && (
					<VolumentalRecommendation
						productId={volumentalProductId!}
						chartId={sizeTable?.toLowerCase()}
						onRecommendation={onVolumentalRecommendation}
					/>
				)}

				<footer id="pdp-atc" className={styles.footerButton}>
					{outOfStock ? (
						<BackInStock key={item?.item} {...buyBox} />
					) : (
						<Button {...buttonProps} size="full" color="red">
							{getButtonLabel()}
						</Button>
					)}
				</footer>
			</section>
		);
	},
);

BuyBox.displayName = "BuyBox";

interface BuyBoxState {
	error?: string;
	success?: boolean;
}

export const BackInStock = ({ getButtonLabel, getButtonProps, item }: UseBuyBoxReturn) => {
	if (!item?.item) return null;

	const { country, language } = useGeo();
	const backInStock = useBackInStock();

	const { data: loggedInEmail } = useSelection({
		select: (data) => data.loggedIn?.email,
	});

	const t = useTranslations();

	const [state, action, pending] = useActionState(
		async (_: BuyBoxState, data: FormData): Promise<BuyBoxState> => {
			if (!item.item || !data.get("email")) {
				return {
					error: t("Invalid values submitted"),
				};
			}

			return backInStock
				.mutateAsync({
					item: item.item,
					email: data.get("email")?.toString(),
					shipTo: {
						country,
					},
					language,
				})
				.then(() => {
					return {
						success: true,
					};
				})
				.catch(() => {
					return {
						error: t("Something went wrong submitting your request"),
					};
				});
		},
		{} as BuyBoxState,
	);

	return (
		<ModalRoot>
			<ModalTrigger asChild>
				<Button {...getButtonProps()} size="full">
					<Translate>Notify me</Translate>
				</Button>
			</ModalTrigger>

			<ModalViewport style={{ minHeight: "0px" }}>
				<ModalContent>
					<ModalMain className={styles.backInStockContent}>
						<header>
							<DrawerClose className={styles.close} />
							<h2 className={styles.title}>
								<Translate>Notify me</Translate>
							</h2>
							<span>
								<Translate>
									Fill in your email and we will let you know if this item gets restocked
								</Translate>
							</span>
						</header>

						<form className={styles.backInStock} action={action}>
							{!state.success && (
								<TextField
									key={loggedInEmail}
									defaultValue={loggedInEmail}
									type="email"
									required
									name="email"
									className={styles.emailField}
									label={t("Email")}
								/>
							)}
							{state.error && <p className={styles.error}>{state.error}</p>}
							{state.success && (
								<p className={styles.success}>
									<Translate>You have successfully subscribed!</Translate>
								</p>
							)}

							{state.success ? (
								<DialogClose asChild>
									<Button type="button">
										<Translate>Close</Translate>
									</Button>
								</DialogClose>
							) : (
								<Button loading={pending} type="submit">
									{getButtonLabel()}
								</Button>
							)}
						</form>
					</ModalMain>
				</ModalContent>
			</ModalViewport>
		</ModalRoot>
	);
};

export const SizeSelector = ({ sizes, getSizeProps }: SizeSelectorProps) => {
	const t = useTranslations();
	const searchParams = useUrlSearchParams();
	const selectedSize = searchParams.get("size");
	if (!sizes?.length) return null;

	if (sizes.length === 1) {
		return null;
	}

	return (
		<div className={styles.sizeList}>
			{sizes.map((item, index) => {
				const isNumeric = !Number.isNaN(Number(item.name));
				const inStock = item.stock !== "no" && item.stock !== 0;
				return (
					<SizeToggle
						data-stock={inStock ? "yes" : "no"}
						circular={isNumeric}
						key={`${item.sizeId}-${index}-${item.name}`}
						{...getSizeProps(item)}
						aria-pressed={selectedSize === item.name ? "true" : "false"}
						aria-label={`${t("Size")} ${item.name}, ${inStock ? t("In stock") : t("Out of stock")}, ${selectedSize === item.name ? t("selected") : t("not selected")}`}
					>
						{item.name}
					</SizeToggle>
				);
			})}
		</div>
	);
};

export function useFormattedMeasurementChart(
	measurementChart?: {
		unit?: string;
		contents?: { content?: string; x?: number; y?: number }[];
		x?: string[];
		y?: string[];
	},
	itemTable?: {
		desc?: string;
		x?: string[];
	},
) {
	const t = useTranslations();

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	return useMemo(() => {
		if (!measurementChart || !measurementChart.x || !measurementChart.y || !itemTable?.x) {
			return { tableData: [], sizeHeaders: [] as string[], sizesOnXAxis: false };
		}

		const xLabels = measurementChart.x;
		const yLabels = measurementChart.y;

		// Detect which axis contains the actual product sizes by checking itemTable.x against both axes
		const matchesXAxis = (colLabel: string, xLabel: string): boolean => {
			if (colLabel === xLabel) return true;
			if (xLabel.endsWith(colLabel) || xLabel.endsWith(` ${colLabel}`)) return true;
			const withoutPrefix = xLabel.replace(/^[A-Z]+\s+/, "");
			return withoutPrefix === colLabel;
		};

		// Count matches in x-axis
		const xMatches = itemTable.x.filter((col) =>
			xLabels.some((xLabel) => matchesXAxis(col, xLabel)),
		).length;

		// Count matches in y-axis
		const yMatches = itemTable.x.filter((col) =>
			yLabels.some((yLabel) => matchesXAxis(col, yLabel)),
		).length;

		// Determine which axis contains the sizes based on match count
		const sizesOnXAxis = xMatches >= yMatches;

		// Follow Centra's structure
		let colLabels: string[];
		let rowLabels: string[];

		if (sizesOnXAxis) {
			// Sizes are on X-axis: use full xLabels that match itemTable.x
			colLabels = xLabels.filter((xLabel) =>
				itemTable.x?.some((size) => matchesXAxis(size, xLabel)),
			);
			rowLabels = yLabels;
		} else {
			// Sizes are on Y-axis: use xLabels as columns, filter yLabels that match itemTable.x
			colLabels = xLabels;
			rowLabels = yLabels.filter((yLabel) =>
				itemTable.x?.some((size) => matchesXAxis(size, yLabel)),
			);
		}

		const byXY = new Map<string, string>();
		for (const it of measurementChart.contents ?? []) {
			if (typeof it.x === "number" && typeof it.y === "number") {
				byXY.set(`${it.x}|${it.y}`, it.content ?? "-");
			}
		}

		const xIdx = new Map(xLabels.map((l, i) => [l, i]));
		const yIdx = new Map(yLabels.map((l, i) => [l, i]));

		// Helper function to find matching index in an axis for a column label
		// Handles cases where colLabel is "19" but axisLabel is "EU 19"
		const findAxisIndex = (colLabel: string, axisIdx: Map<string, number>): number => {
			// Try exact match first
			if (axisIdx.has(colLabel)) {
				return axisIdx.get(colLabel)!;
			}

			// Try to find axisLabel that contains the colLabel
			for (const [axisLabel, index] of axisIdx.entries()) {
				if (matchesXAxis(colLabel, axisLabel)) {
					return index;
				}
			}

			return -1;
		};

		const headerRow = [t("Size"), ...colLabels];

		const dataRows = rowLabels.map((rowLabel) => {
			const row = [rowLabel];

			for (const colLabel of colLabels) {
				let xi: number;
				let yi: number;

				if (sizesOnXAxis) {
					// Sizes are on x-axis (colLabel), measurements are on y-axis (rowLabel)
					xi = findAxisIndex(colLabel, xIdx);
					yi = yIdx.get(rowLabel) ?? -1;
				} else {
					// Sizes are on y-axis (rowLabel), measurements are on x-axis (colLabel)
					xi = xIdx.get(colLabel) ?? -1;
					yi = findAxisIndex(rowLabel, yIdx);
				}

				row.push(byXY.get(`${xi}|${yi}`) ?? "-");
			}

			return row;
		});

		const tableData = [headerRow, ...dataRows];
		return {
			tableData,
			sizeHeaders: colLabels,
			sizesOnXAxis,
		};
	}, [t, itemTable?.x, measurementChart?.x, measurementChart?.y, measurementChart?.contents]);
}
